/**
 * The single source of truth for "what calendar day is it?" across the product.
 *
 * WHY THIS EXISTS
 *
 * Before this module the codebase answered that question four different ways, each
 * with a comment claiming it matched one of the others:
 *
 *   - `formatDateUTC(new Date())`            (apps/web, db-tenant)
 *   - `balanceDateUTC()`                     (packages/tasks, LedgerSync)
 *   - `dayjs.utc().format('YYYY-MM-DD')`     (packages/tasks, Plaid)
 *   - `new Date().toISOString().slice(0,10)` (supabase/functions, Deno)
 *
 * ...while a fifth family (the Cash Position period ladder, the report's "As of"
 * header, the projection write path) used the BROWSER's local day. Both families
 * meet on the same `balances.date` / `projections.date` columns, and the mismatch
 * is not theoretical: a US user entering Friday's closing balance at 19:30 Central
 * files it under Saturday in UTC, so a reader asking for "Friday" does not find it.
 * That shipped as a real defect (DCP-161) before this module existed.
 *
 * The fix is not "pick UTC" or "pick the browser" — both are wrong for a treasury
 * product. The right day is the one the COMPANY's books are on. That is what this
 * module returns, and every writer and reader of a dated row must go through it, so
 * a read can never disagree with the write that produced the row.
 *
 * WHAT THIS DOES *NOT* FIX — ALREADY-STORED ROWS (read before touching a read path)
 *
 * This changes the MEANING of a value written from here on; it does not rewrite
 * history, and no migration backfills one. Rows written by the old UTC-day code
 * carry the day that was current in UTC at the moment of the write, which for a
 * write between roughly 18:00–23:59 Central is TOMORROW's date. Concretely:
 *
 *   - Readers comparing with `>=` (the DCP-161 coverage signal,
 *     `getCoverageByFacility`) are unaffected in the direction that matters: an old
 *     row stamped one day LATE still satisfies "on or after the last expected
 *     posting day", so no false missing-data warning comes out of this.
 *   - Readers comparing an EXACT day are not. The manual-precedence guard in the two
 *     snapshot writers (`.eq('date', today)` in `applyBalanceSnapshot` and
 *     `ensureBalanceSnapshot`) will not recognise a hand-entered balance that the old
 *     code filed under the following day, so an API sync can overwrite it. Per-day
 *     reporting reads the same way: such a row appears on the wrong day, forever.
 *
 * So a tenant's `balances.date` history has a discontinuity at the deploy of this
 * module: correct after, off-by-one-for-evening-writes before. That is a deliberate,
 * recorded trade — the alternative is a backfill, which needs a per-tenant answer to
 * "how many evening writes actually exist" before it can be written safely, and
 * guessing wrong there corrupts good rows. If that answer ever arrives, the backfill
 * belongs in its own migration and ticket, not here.
 *
 * HOW THE DAY IS DERIVED
 *
 * dayjs is this product's date utility, so this module uses it too, through the
 * configured instance in `./dayjs.ts`: its `timezone` plugin is what makes
 * `.tz(BUSINESS_TIMEZONE)` possible at all, and `customParseFormat` is what makes
 * the strict parsing below actually strict.
 *
 * Two non-obvious consequences of that choice, both handled explicitly:
 *
 *   1. dayjs does NOT throw on bad input — `dayjs(new Date('nonsense'))` yields an
 *      object whose `.format()` returns the literal string "Invalid Date". For a
 *      primitive this widely called that is the silent degradation this module
 *      exists to eliminate ("Invalid Date" would travel straight into a SQL
 *      predicate), so every entry point validates and throws a named `TypeError`.
 *      Note the ORDER in `businessDate`: validity is checked BEFORE `.tz()`, so a
 *      bad Date still reports as a `TypeError` rather than being masked by the
 *      `RangeError` an invalid zone name would raise.
 *   2. dayjs's `timezone` plugin is itself implemented on top of
 *      `Intl.DateTimeFormat` — there is no other mechanism for IANA zones in JS, so
 *      Intl still does the zone arithmetic underneath. What changed is that no call
 *      site in this repo has to reason about it.
 *
 * A note for whoever touches the edge functions: this module is imported by
 * `supabase/functions/_shared/ledgersync-finalize.ts`, so `dayjs` and `dayjs/` MUST
 * stay mapped in `supabase/functions/import_map.json` or that deploy breaks.
 */
import dayjs from './dayjs.ts'

/**
 * The timezone the organisation's books are kept in.
 *
 * A single global constant, because the data model has no per-organisation
 * timezone today: `public.organizations` has no such column, and the `timezone`
 * key seeded into its `settings` JSONB is read by nothing. The value matches the
 * two places that already assume a business calendar — the Sage GL reporting
 * period and the `America/Chicago` cron schedules — and the customer's facilities
 * are Central-time.
 *
 * When per-tenant timezones are needed, `settings->>'timezone'` is the escape
 * hatch and every call site here already takes an explicit override parameter.
 */
export const BUSINESS_TIMEZONE = 'America/Chicago'

/** The only shape the calendar helpers below accept, as a dayjs format string. */
const CALENDAR_DATE_FORMAT = 'YYYY-MM-DD'

/** Exactly `YYYY-MM-DD`, used to reject the wrong SHAPE of value up front. */
const CALENDAR_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/**
 * The current business day as a `YYYY-MM-DD` calendar date.
 *
 * This is what every `DATE` column should be stamped with, and what every
 * "is today's data there?" check should compare against. Use it INSTEAD of
 * `new Date().toISOString().slice(0, 10)` (that is the UTC day, which is a
 * different day for several hours every evening in the Americas) and instead of
 * a bare `dayjs().format('YYYY-MM-DD')` (that is the VIEWER's day, which makes
 * the same record read differently for a user in Warsaw and a user in Houston).
 *
 * @param instant - the point in time to resolve; defaults to now
 * @param timeZone - override for the business timezone; defaults to
 *   {@link BUSINESS_TIMEZONE}. Present so a future per-tenant setting can be
 *   threaded through without touching call sites.
 * @returns the calendar date in `YYYY-MM-DD` form
 * @throws TypeError when `instant` is an invalid Date
 */
export function businessDate(
  instant: Date = new Date(),
  timeZone: string = BUSINESS_TIMEZONE,
): string {
  const parsed = dayjs(instant)

  // See consequence (1) in the module header: without this guard an unparseable
  // Date formats to the string "Invalid Date" and reaches callers as if it were a
  // real day. Checked before `.tz()` so the message names the actual mistake.
  if (!parsed.isValid()) {
    throw new TypeError('businessDate() received an invalid Date')
  }

  return parsed.tz(timeZone).format(CALENDAR_DATE_FORMAT)
}

/**
 * Whether `value` names a calendar date that actually exists.
 *
 * The boolean sibling of {@link parseCalendarDate}, for the callers that must
 * REJECT bad input rather than throw on it — form validation and display
 * formatters. It lives here so the product has one answer to "is this a real
 * calendar day?": both tiers of the check (shape, then existence) and the
 * reasoning behind them are documented on `parseCalendarDate` below.
 *
 * The tier that is easy to get wrong is the second one. A lenient
 * `dayjs(value).isValid()` returns TRUE for `'2026-02-30'`, `'2026-13-01'` and
 * `'2023-02-29'` — its default parser rolls the overflow over (to 1 Mar 2026,
 * 1 Jan 2027 and 1 Mar 2023 respectively) instead of rejecting it, so a caller
 * relying on `isValid()` alone silently accepts, stores and displays a
 * DIFFERENT day than the one it was given.
 *
 * @param value - the string to check; any non-`YYYY-MM-DD` shape is false
 * @returns true only for a well-shaped, real calendar date
 */
export function isCalendarDate(value: string): boolean {
  return (
    CALENDAR_DATE_PATTERN.test(value) && dayjs.utc(value, CALENDAR_DATE_FORMAT, true).isValid()
  )
}

/**
 * Parses a bare calendar date in UTC mode, rejecting anything malformed.
 *
 * Parsed with `dayjs.utc` on purpose: the input is a calendar date with no time or
 * zone of its own, so it must not be shifted by the host's offset — otherwise the
 * weekday, and therefore the answer, becomes machine-dependent.
 *
 * The validation is deliberately TWO-TIER, because the two failures are different
 * mistakes and the caller needs to be told which one they made:
 *
 *   - The regex rejects the wrong SHAPE. It is not redundant with strict parsing:
 *     it pins the contract to a bare calendar date, so a full ISO instant
 *     (`2026-08-13T10:00:00Z`) is reported as the wrong TYPE of value rather than
 *     lumped in with nonsense — every caller here reads a `DATE` column.
 *   - Strict parsing then rejects a well-shaped day that does not EXIST. That is
 *     the nastier input: plain `new Date('2026-02-30T00:00:00Z')` does not fail, it
 *     quietly rolls over to 2026-03-02, so a validity check alone would return a
 *     confident answer for a day the caller never asked about. `customParseFormat`
 *     in strict mode reports 2026-02-30, 2026-04-31 and 2026-13-01 as invalid
 *     (verified), which is what removes the hand-rolled round-trip check this
 *     function used to need.
 *
 * @param date - calendar date in `YYYY-MM-DD` form
 * @param fnName - the public function to name in the error, so the message points
 *   at the call site the caller actually wrote
 * @returns a dayjs object in UTC mode carrying the calendar day
 * @throws TypeError when `date` is not a well-formed, real calendar date
 */
function parseCalendarDate(date: string, fnName: string): ReturnType<typeof dayjs> {
  if (!CALENDAR_DATE_PATTERN.test(date)) {
    throw new TypeError(
      `${fnName}() expects a ${CALENDAR_DATE_FORMAT} calendar date, received: ${date}`,
    )
  }

  const parsed = dayjs.utc(date, CALENDAR_DATE_FORMAT, true)
  if (!parsed.isValid()) {
    throw new TypeError(`${fnName}() received an invalid calendar date: ${date}`)
  }

  return parsed
}

/**
 * Whether a calendar date falls on a weekday (Mon–Fri).
 *
 * NOTE: there is no bank-holiday calendar anywhere in this product, so a federal
 * holiday still counts as a business day here.
 *
 * @param date - calendar date in `YYYY-MM-DD` form
 * @returns true for Monday through Friday
 * @throws TypeError when `date` is not a well-formed calendar date
 */
export function isBusinessDay(date: string): boolean {
  // `.day()` on a UTC-mode instance is the UTC weekday, which is what a bare
  // calendar date means — the answer must not depend on the host's offset.
  const dayOfWeek = parseCalendarDate(date, 'isBusinessDay').day()
  return dayOfWeek !== 0 && dayOfWeek !== 6
}

/**
 * The most recent weekday on or before the given calendar date.
 *
 * Used wherever "the last day data was expected" is the right question rather
 * than "today": banks post no balances at the weekend, so comparing against a
 * Saturday would report every account as missing data all weekend.
 *
 * @param date - calendar date in `YYYY-MM-DD` form
 * @returns the same date on Mon–Fri, otherwise the preceding Friday
 * @throws TypeError when `date` is not a well-formed calendar date
 */
export function lastBusinessDay(date: string): string {
  const parsed = parseCalendarDate(date, 'lastBusinessDay')
  const dayOfWeek = parsed.day()
  const daysBack = dayOfWeek === 0 ? 2 : dayOfWeek === 6 ? 1 : 0
  // dayjs objects are immutable, so `subtract` returns a new instance rather than
  // mutating `parsed` the way the previous `setUTCDate` version did. Subtracting in
  // UTC mode is also DST-immune: no day is 23 or 25 hours long there.
  return parsed.subtract(daysBack, 'day').format(CALENDAR_DATE_FORMAT)
}
